<script>
import Layout from '@layouts/main'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

import ContaPagarReceber from '@/src/services/conta-pagar-receber'
import {
  buildAgendaItems,
  formatCurrency,
  formatDate,
} from './finance-helpers'

export default {
  components: { Layout, Loading },
  data() {
    return {
      isLoading: false,
      filtro: '',
      filtroTipo: 'todos',
      filtroStatus: 'todos',
      agenda: [],
      fields: [
        { key: 'tipo_operacao', label: 'Tipo' },
        { key: 'descricaoConta', label: 'Descricao' },
        { key: 'cliente', label: 'Pessoa' },
        { key: 'data_competencia', label: 'Competencia' },
        { key: 'data_vencimento', label: 'Vencimento' },
        { key: 'valorNumerico', label: 'Valor' },
        { key: 'statusLabel', label: 'Status' },
        { key: 'actions', label: '' },
      ],
    }
  },
  mounted() {
    this.carregarAgenda()
  },
  computed: {
    itensFiltrados() {
      return this.agenda.filter((item) => {
        const descricao = (item.descricaoConta || '').toLowerCase()
        const pessoa = (item.cliente || '').toLowerCase()
        const termo = this.filtro.toLowerCase()
        const matchesText =
          !termo || descricao.includes(termo) || pessoa.includes(termo)
        const matchesTipo =
          this.filtroTipo === 'todos' || item.tipo_operacao === this.filtroTipo
        const matchesStatus =
          this.filtroStatus === 'todos' || item.status === this.filtroStatus

        return matchesText && matchesTipo && matchesStatus
      })
    },
  },
  methods: {
    async carregarAgenda() {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true

      try {
        const [payablesResponse, receivablesResponse] = await Promise.all([
          ContaPagarReceber.listar(token, 'PAGAR', 1, 500),
          ContaPagarReceber.listar(token, 'RECEBER', 1, 500),
        ])

        this.agenda = buildAgendaItems(
          payablesResponse.data.contas || [],
          receivablesResponse.data.contas || []
        )
      } catch (error) {
        this.$bvToast.toast(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
            'Nao foi possivel carregar a agenda financeira.',
          {
            variant: 'danger',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          }
        )
      } finally {
        this.isLoading = false
      }
    },
    formatCurrencyValue(value) {
      return formatCurrency(value)
    },
    formatDateValue(value) {
      return formatDate(value)
    },
    statusClass(status) {
      return `finance-status-badge is-${status}`
    },
    tipoClass(tipo) {
      return `finance-type-chip ${tipo === 'RECEBER' ? 'is-receber' : 'is-pagar'}`
    },
    goTo(item) {
      this.$router.push(
        item.tipo_operacao === 'RECEBER'
          ? '/financeiro/contas-a-receber'
          : '/financeiro/contas-a-pagar'
      )
    },
  },
}
</script>

<template>
  <Layout>
    <div class="finance-shell">
      <section class="finance-hero compact">
        <div>
          <span class="finance-eyebrow">Planejamento</span>
          <h1 class="finance-title">Agenda Financeira</h1>
          <p class="finance-subtitle">
            Uma unica fila para acompanhar vencimentos de contas a pagar e a
            receber.
          </p>
        </div>
        <div class="finance-actions">
          <button class="btn btn-light" @click="$router.push('/financeiro/dashboard')">
            Dashboard
          </button>
          <button class="btn btn-webreceita" @click="$router.push('/financeiro/fluxo-de-caixa')">
            Fluxo de caixa
          </button>
        </div>
      </section>

      <section class="finance-panel">
        <div class="finance-filters">
          <b-form-input v-model="filtro" placeholder="Buscar por descricao ou pessoa" />
          <b-form-select
            v-model="filtroTipo"
            :options="[
              { value: 'todos', text: 'Todos os tipos' },
              { value: 'PAGAR', text: 'A pagar' },
              { value: 'RECEBER', text: 'A receber' },
            ]"
          />
          <b-form-select
            v-model="filtroStatus"
            :options="[
              { value: 'todos', text: 'Todos os status' },
              { value: 'overdue', text: 'Vencidos' },
              { value: 'today', text: 'Hoje' },
              { value: 'upcoming', text: 'Em breve' },
              { value: 'open', text: 'Em aberto' },
            ]"
          />
        </div>

        <b-table
          small
          responsive
          hover
          :items="itensFiltrados"
          :fields="fields"
          show-empty
          class="finance-table"
        >
          <template #empty>
            <div class="finance-empty-state">
              Nenhum titulo encontrado com os filtros atuais.
            </div>
          </template>
          <template #cell(tipo_operacao)="row">
            <span :class="tipoClass(row.item.tipo_operacao)">
              {{ row.item.tipo_operacao === 'RECEBER' ? 'Receber' : 'Pagar' }}
            </span>
          </template>
          <template #cell(data_competencia)="row">
            {{ formatDateValue(row.item.data_competencia) }}
          </template>
          <template #cell(data_vencimento)="row">
            {{ formatDateValue(row.item.data_vencimento) }}
          </template>
          <template #cell(valorNumerico)="row">
            {{ formatCurrencyValue(row.item.valorNumerico) }}
          </template>
          <template #cell(statusLabel)="row">
            <span :class="statusClass(row.item.status)">
              {{ row.item.statusLabel }}
            </span>
          </template>
          <template #cell(actions)="row">
            <button class="btn btn-light btn-sm" @click="goTo(row.item)">
              Abrir
            </button>
          </template>
        </b-table>
      </section>

      <b-overlay :show="isLoading" no-wrap />
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.finance-shell {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.finance-hero,
.finance-panel {
  background: #fff;
  border: 1px solid rgba(0, 142, 117, 0.14);
  border-radius: 20px;
  box-shadow: 0 18px 34px rgba(15, 61, 52, 0.06);
}

.finance-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 30px;
}

.finance-hero.compact {
  align-items: center;
}

.finance-eyebrow {
  display: inline-block;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #008e75;
}

.finance-title {
  margin: 0 0 8px;
  font-size: 34px;
  color: #11493f;
}

.finance-subtitle {
  margin: 0;
  color: #64807a;
}

.finance-actions {
  display: flex;
  gap: 12px;
}

.finance-panel {
  padding: 24px;
}

.finance-filters {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) 220px 220px;
  gap: 14px;
  margin-bottom: 18px;
}

.finance-table::v-deep table {
  margin-bottom: 0;
}

.finance-empty-state {
  padding: 24px 12px;
  text-align: center;
  color: #6f8a84;
}

.finance-type-chip,
.finance-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.finance-type-chip.is-receber {
  background: rgba(0, 142, 117, 0.1);
  color: #0d6555;
}

.finance-type-chip.is-pagar {
  background: rgba(214, 58, 58, 0.1);
  color: #bf3030;
}

.finance-status-badge.is-overdue {
  background: rgba(214, 58, 58, 0.12);
  color: #bf3030;
}

.finance-status-badge.is-today {
  background: rgba(240, 145, 31, 0.14);
  color: #b86e12;
}

.finance-status-badge.is-upcoming,
.finance-status-badge.is-open,
.finance-status-badge.is-unknown {
  background: rgba(0, 142, 117, 0.1);
  color: #0d6555;
}

@media (max-width: 991px) {
  .finance-filters {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .finance-hero {
    flex-direction: column;
  }
}
</style>
